/**
 * Générateur — Multiplier par 0,1 / 0,01 / 0,001
 * Attendu ID : 610 | Niveau : 6ème | Domaine : NC
 *
 * Types générés :
 *   - multiplier_01_qcm_unique
 *   - multiplier_01_qcm_multiple
 *   - multiplier_01_texte       (saisie)
 *   - multiplier_01_relier
 *   - multiplier_01_tableau_vf
 *   - multiplier_01_texte_trou_drag
 *   - multiplier_01_texte_trou_saisie
 *
 * Règles dures de la fiche cluster 7 (section ID 610) :
 *   - Constantes : 0,1 / 0,01 / 0,001 UNIQUEMENT.
 *   - Facteur libre : entier 1..9999 ou décimal avec 1 à 4 décimales.
 *   - Au moins 1 exercice par famille sur un lot global :
 *       (1) entier × 0,1     → décimal à 1 rang
 *       (2) entier × 0,01    → décimal à 2 rangs
 *       (3) entier × 0,001   → décimal à 3 rangs
 *       (4) décimal × 0,1/0,01/0,001
 *       (5) fait mémorisé (0,1×0,1, 10×0,1, etc.)
 *       (6) zéros intercalaires (7 × 0,001 = 0,007)
 *   - Distracteurs : au moins 3 familles cognitives distinctes du pool
 *     (surgénéralisation, confusion de rangs, inversion, addition).
 *
 * Mécanismes-clés testés :
 *   - Décalage de virgule de 1/2/3 rangs vers la gauche
 *   - Retombée sous l'unité (45 × 0,01 = 0,45)
 *   - Zéros intercalaires (8 × 0,001 = 0,008)
 *   - Retombée à l'entier (600 × 0,01 = 6)
 *   - Faits mémorisés entre constantes
 *
 * Variantes estimées : > 4000
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 610;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Les 3 constantes du cluster, avec leur nombre de rangs de décalage. */
const CONSTANTES = [
    { str: '0,1',   k: 1, divStr: '10',    divKatex: '10' },
    { str: '0,01',  k: 2, divStr: '100',   divKatex: '100' },
    { str: '0,001', k: 3, divStr: '1 000', divKatex: '1\\,000' },
];

// ─── Labels ───
const L_QCM = 'Calculer un produit par 0,1 / 0,01 / 0,001';
const L_QCM_MULTI = 'Identifier les multiplications correctes par 0,1 / 0,01 / 0,001';
const L_SAISIE = 'Taper le résultat d\'une multiplication par 0,1 / 0,01 / 0,001';
const L_RELIER = 'Associer un produit par 0,1 / 0,01 / 0,001 à son résultat';
const L_VF = 'Vrai/Faux sur des multiplications par 0,1 / 0,01 / 0,001';
const L_TROU_DRAG = 'Compléter des multiplications par 0,1 / 0,01 / 0,001 (drag)';
const L_TROU_SAISIE = 'Compléter des multiplications par 0,1 / 0,01 / 0,001 (saisie)';
const L_CONVERSION = 'Convertir des mesures (appliquer × 0,1 / 0,01 / 0,001)';

// ─── Helpers numériques ───

/**
 * Décale la virgule de `k` rangs vers la gauche, de manière exacte (sans
 * passer par IEEE 754, qui donne 3.14 * 0.1 = 0.31400000000000006).
 * Travail en string.
 *
 * Ex : shiftLeft("45",   1) → "4,5"
 *      shiftLeft("45",   2) → "0,45"
 *      shiftLeft("45",   3) → "0,045"
 *      shiftLeft("3,14", 1) → "0,314"
 *      shiftLeft("3,14", 2) → "0,0314"
 *      shiftLeft("600",  2) → "6"
 *      shiftLeft("12,5", 2) → "0,125"
 *      shiftLeft("240",  1) → "24"
 *      shiftLeft("0,1",  1) → "0,01"
 *      shiftLeft("10",   1) → "1"
 */
const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    const totalLen = allDigits.length;

    if (newDecs < totalLen) {
        // Partie entière non nulle
        const newInt = allDigits.slice(0, totalLen - newDecs);
        const newDec = allDigits.slice(totalLen - newDecs);
        const newDecTrim = newDec.replace(/0+$/, '');
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    // newDecs ≥ totalLen : on préfixe de zéros sous la virgule
    const zeros = '0'.repeat(newDecs - totalLen);
    const rawDec = zeros + allDigits;
    // Enlever les zéros de fin (mais les zéros intercalaires doivent rester)
    const decTrim = rawDec.replace(/0+$/, '');
    if (decTrim === '') return '0';
    return `0,${decTrim}`;
};

/** Teste si un string représente le même décimal que son argument (4,5 ≡ 4,50). */
const sameDecimal = (a, b) => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.'));
    return Math.abs(toNum(a) - toNum(b)) < 1e-12;
};

/** Décale la virgule VERS LA DROITE de k rangs (pour le piège surgénéralisation). */
const shiftRight = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const oldDecs = decPart.length;
    const newDecs = oldDecs - k;
    if (newDecs >= 0) {
        // Nouvelle partie entière : allDigits.slice(0, totalLen - newDecs)
        const totalLen = allDigits.length;
        const newInt = allDigits.slice(0, totalLen - newDecs) || '0';
        const newDec = allDigits.slice(totalLen - newDecs);
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        const newDecTrim = newDec.replace(/0+$/, '');
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    // newDecs < 0 : on ajoute des zéros à droite (nombre ×10, ×100...)
    const zeros = '0'.repeat(-newDecs);
    const fullInt = allDigits + zeros;
    const trimmed = fullInt.replace(/^0+/, '') || '0';
    return trimmed;
};

// ─── Pools de facteurs libres ───

/**
 * Pool d'entiers conçu pour couvrir :
 *  - Cas canoniques (45, 8, 7, 12, 25, 32)
 *  - Retombée à l'entier après × 0,01 ou × 0,001 (100, 1000, 600, 2500, 240)
 *  - Cas chiffre unique (3, 7, 8, 9)
 *  - Cas intermédiaires (50, 120, 320, 75, 800)
 */
const POOL_ENTIERS = [
    3, 5, 7, 8, 9, 12, 15, 18, 25, 32, 45, 50, 60, 75, 80, 100, 120, 150,
    200, 240, 320, 500, 600, 750, 800, 1000, 1200, 1500, 2000, 2500,
];

/**
 * Pool de décimaux conçu pour couvrir la famille 4 (décimal × 0,1/0,01/0,001).
 * Chaque décimal a 1 à 3 décimales et ne finit pas par un zéro (éviter
 * les formes ambiguës comme "3,50" dans l'énoncé). Les produits restent
 * tous des décimaux finis lisibles.
 */
const POOL_DECIMAUX = [
    '1,5', '2,4', '2,7', '3,14', '3,7', '4,8', '5,6', '6,5', '7,8', '8,2',
    '9,5', '10,5', '12,5', '14,7', '25,6', '3,25', '6,75', '18,4', '42,5',
];

/**
 * Faits mémorisés extraits du cours (ligne 220-227).
 * Chacun est une égalité vraie de la forme "a × c = r".
 */
const FAITS_MEMORISES = [
    { a: '10',   c: '0,1',   r: '1' },
    { a: '100',  c: '0,01',  r: '1' },
    { a: '1 000', c: '0,001', r: '1' }, // notation avec espace milliers
    { a: '0,1',  c: '0,1',   r: '0,01' },
    { a: '0,1',  c: '0,01',  r: '0,001' },
    { a: '0,01', c: '0,1',   r: '0,001' },
    { a: '10',   c: '0,01',  r: '0,1' },
    { a: '10',   c: '0,001', r: '0,01' },
    { a: '100',  c: '0,001', r: '0,1' },
];

/** Retourne un facteur libre aléatoire (entier ou décimal) sous forme string. */
const randFacteur = ({ decimalProba = 0.35 } = {}) => {
    if (Math.random() < decimalProba) return randomChoice(POOL_DECIMAUX);
    return String(randomChoice(POOL_ENTIERS));
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Calculer un produit par 0,1 / 0,01 / 0,001
// ═══════════════════════════════════════════════════════════

/** Espace des milliers à l'AFFICHAGE (revue 2026-08-23, choix prof) :
 *  « 500000 » → « 500 000 ». Les calculs internes restent sans espace. */
const espMilliers = (s) => {
    const [ent, dec] = String(s).replace(/[\s ]/g, '').split(',');
    const grp = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return dec === undefined ? grp : `${grp},${dec}`;
};

/** Applique l'espace des milliers à tous les nombres d'un texte affiché. */
const espMilliersTexte = (t) => String(t).replace(/\d+(?:,\d+)?/g, (m) => espMilliers(m));
/** Idem pour une phrase à trous : ne touche JAMAIS l'intérieur des {{trous}}
 *  (le correcteur compare mot à mot ce que l'élève saisit/glisse). */
const espMilliersHorsTrous = (phrase) => String(phrase)
    .split(/(\{\{[^}]*\}\})/g)
    .map((seg) => (seg.startsWith('{{') ? seg : espMilliersTexte(seg)))
    .join('');

const FORMULATIONS_QCM = [
    (a, c) => `<p>Combien fait ${a} × ${c} ?</p>`,
    (a, c) => `<p>Calculer ${a} × ${c}.</p>`,
    (a, c) => `<p>Donner le résultat de ${a} × ${c}.</p>`,
    (a, c) => `<p>Quel est le produit de ${a} par ${c} ?</p>`,
];

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureQcm610 = null;

const genMultQcm = () => {
    let payload = construireMultQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm610; t++) {
        payload = construireMultQcm();
    }
    derniereSignatureQcm610 = payload.texte;
    return payload;
};

const construireMultQcm = () => {
    // 20% des cas : fait mémorisé (pour couvrir la famille 5)
    if (Math.random() < 0.2) {
        const fait = randomChoice(FAITS_MEMORISES);
        const correct = fait.r;

        // Distracteurs pour un fait mémorisé : variations simples
        const dist = new Set();
        // Piège "plus grand" : a × 10^k
        const cObj = CONSTANTES.find((x) => x.str === fait.c);
        dist.add(shiftRight(fait.a, cObj.k));
        // Piège "identité" : réponse égale au facteur a
        dist.add(fait.a);
        // Piège "addition" : a + c
        {
            const aNumF = parseFloat(String(fait.a).replace(/\s/g, '').replace(',', '.'));
            const cNumF = parseFloat(String(fait.c).replace(',', '.'));
            let addF = (aNumF + cNumF).toFixed(4).replace('.', ',');
            addF = addF.replace(/0+$/, '').replace(/,$/, '');
            dist.add(addF);
        }

        // Nettoyer doublons/collision avec la bonne réponse
        const arr = [...dist].filter((v) => !sameDecimal(v, correct));
        // Compléter à 3 si nécessaire
        const fallback = [
            shiftLeft(fait.a, cObj.k + 1),
            shiftLeft(fait.a, Math.max(1, cObj.k - 1)),
            '0',
        ];
        let idx = 0;
        while (arr.length < 3 && idx < fallback.length) {
            const v = fallback[idx++];
            if (!sameDecimal(v, correct) && !arr.some((x) => sameDecimal(x, v))) arr.push(v);
        }

        return makeQcm(
            [correct, ...arr.slice(0, 3)].map(espMilliers),
            espMilliers(correct),
            randomChoice(FORMULATIONS_QCM)(espMilliers(fait.a), fait.c),
            opts(L_QCM),
        );
    }

    // Cas général
    const constante = randomChoice(CONSTANTES);
    const a = randFacteur();
    const correct = shiftLeft(a, constante.k);

    const dist = new Set();

    // (1) Surgénéralisation "plus grand" : a × 10^k au lieu de a / 10^k
    dist.add(shiftRight(a, constante.k));

    // (2) Confusion de rangs — trop peu de rangs (k-1)
    if (constante.k > 1) dist.add(shiftLeft(a, constante.k - 1));
    // (2 bis) Confusion de rangs — trop de rangs (k+1)
    if (constante.k < 3) dist.add(shiftLeft(a, constante.k + 1));

    // (3) Addition au lieu de multiplication : a + c (hack simple mais diagnostique)
    const aNum = parseFloat(a.replace(',', '.'));
    const cNum = parseFloat(constante.str.replace(',', '.'));
    const addRaw = aNum + cNum;
    // Formater avec au maximum 3 décimales (convient aux constantes 0,1/0,01/0,001)
    let addStr = addRaw.toFixed(3).replace('.', ',');
    // Strip trailing zeros comme la fonction shiftLeft
    addStr = addStr.replace(/0+$/, '').replace(/,$/, '');
    dist.add(addStr);

    // (4) Fallback : l'identité (réponse = facteur libre, comme si × 1)
    dist.add(a);

    // Retirer collisions
    const arr = [...dist].filter((v) => !sameDecimal(v, correct));
    const pool = arr.slice(0, 3);
    while (pool.length < 3) {
        const extra = shiftLeft(a, constante.k + pool.length + 1);
        if (!sameDecimal(extra, correct) && !pool.some((x) => sameDecimal(x, extra))) pool.push(extra);
        else pool.push(`${pool.length}`);
    }

    return makeQcm(
        [correct, ...pool.slice(0, 3)].map(espMilliers),
        espMilliers(correct),
        randomChoice(FORMULATIONS_QCM)(espMilliers(a), constante.str),
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — Parmi ces égalités, lesquelles sont vraies ?
// ═══════════════════════════════════════════════════════════

const genMultQcmMultiple = () => {
    const propositions = []; // { text, isVrai }
    const used = new Set();

    // Déduplication sur la forme NORMALISÉE, celle que voit le scoring : il retire les espaces,
    // y compris les espaces de milliers posés à l'affichage. Sur le texte brut, « 1000 × 0,001 = 1 »
    // et « 1 000 × 0,001 = 1 » passaient pour deux propositions distinctes et se retrouvaient toutes
    // deux dans la liste : l'élève qui cochait la seconde était compté faux (A-QCM-NORM-DUP, 1 tirage
    // sur 100, corrigé le 2026-09-22).
    const cleNormalisee = (text) => String(text).replace(/[\s  ]/g, '');
    const addProp = (text, isVrai) => {
        const cle = cleNormalisee(text);
        if (used.has(cle)) return false;
        used.add(cle);
        propositions.push({ text, isVrai });
        return true;
    };

    // 3 propositions VRAIES variées :

    // (V1) Cas canonique : entier × une des 3 constantes
    {
        const c = randomChoice(CONSTANTES);
        const a = String(randomChoice(POOL_ENTIERS));
        addProp(`${a} × ${c.str} = ${shiftLeft(a, c.k)}`, true);
    }

    // (V2) Fait mémorisé
    {
        const fait = randomChoice(FAITS_MEMORISES);
        addProp(`${fait.a} × ${fait.c} = ${fait.r}`, true);
    }

    // (V3) Décimal × constante (famille 4) — couvre le cas décimal préexistant
    {
        for (let tries = 0; tries < 20; tries++) {
            const c = randomChoice(CONSTANTES);
            const a = randomChoice(POOL_DECIMAUX);
            const text = `${a} × ${c.str} = ${shiftLeft(a, c.k)}`;
            if (addProp(text, true)) break;
        }
    }

    // 3 propositions FAUSSES, 3 familles distinctes :

    // (F1) Surgénéralisation "plus grand" — ×10^k au lieu de ×0,... 10^-k
    {
        for (let tries = 0; tries < 20; tries++) {
            const c = randomChoice(CONSTANTES);
            const a = String(randomChoice(POOL_ENTIERS));
            const faux = shiftRight(a, c.k);
            const correct = shiftLeft(a, c.k);
            if (sameDecimal(faux, correct)) continue;
            if (addProp(`${a} × ${c.str} = ${faux}`, false)) break;
        }
    }

    // (F2) Confusion de rangs — shifter du mauvais nombre de rangs
    {
        for (let tries = 0; tries < 20; tries++) {
            const c = randomChoice(CONSTANTES);
            const a = String(randomChoice(POOL_ENTIERS));
            const wrongK = c.k === 1 ? 2 : (c.k === 3 ? 2 : randomChoice([1, 3]));
            const faux = shiftLeft(a, wrongK);
            const correct = shiftLeft(a, c.k);
            if (sameDecimal(faux, correct)) continue;
            if (addProp(`${a} × ${c.str} = ${faux}`, false)) break;
        }
    }

    // (F3) Addition au lieu de multiplication (ex : 45 × 0,1 = 45,1)
    {
        for (let tries = 0; tries < 20; tries++) {
            const c = randomChoice(CONSTANTES);
            const a = String(randomChoice(POOL_ENTIERS));
            // a + c en format français
            const aNum = parseFloat(a.replace(',', '.'));
            const cNum = parseFloat(c.str.replace(',', '.'));
            let addStr = (aNum + cNum).toFixed(3).replace('.', ',').replace(/0+$/, '').replace(/,$/, '');
            const correct = shiftLeft(a, c.k);
            if (sameDecimal(addStr, correct)) continue;
            if (addProp(`${a} × ${c.str} = ${addStr}`, false)) break;
        }
    }

    // Revue 2026-08-23 (choix prof) : 5 propositions au lieu de 6 — on retire
    // d'abord une fausse au hasard, les 3 familles restent en rotation.
    const CIBLE_PROPS = 5;
    {
        const fausses = propositions.filter((p) => !p.isVrai);
        const retiree = fausses.length ? randomChoice(fausses) : randomChoice(propositions);
        propositions.splice(propositions.indexOf(retiree), 1);
    }

    // Quota VARIABLE de vraies (revue 2026-08-19) : le 3V/3F constant était
    // comptable — l'élève cochait toujours 3 cases. On vise 2 à 4 vraies.
    const nbVrai = randInt(2, 4);
    const ajouterVraie = () => {
        for (let t = 0; t < 30; t++) {
            const c = randomChoice(CONSTANTES);
            const a = String(randomChoice(POOL_ENTIERS));
            if (addProp(`${a} × ${c.str} = ${shiftLeft(a, c.k)}`, true)) return true;
        }
        return false;
    };
    const ajouterFausse = () => {
        for (let t = 0; t < 30; t++) {
            const c = randomChoice(CONSTANTES);
            const a = String(randomChoice(POOL_ENTIERS));
            const faux = shiftRight(a, c.k);
            if (sameDecimal(faux, shiftLeft(a, c.k))) continue;
            if (addProp(`${a} × ${c.str} = ${faux}`, false)) return true;
        }
        return false;
    };
    // Ajuster vers le quota : retirer/ajouter en gardant CIBLE_PROPS propositions.
    for (let garde = 0; garde < 10; garde++) {
        const vraies = propositions.filter((p) => p.isVrai);
        const fausses = propositions.filter((p) => !p.isVrai);
        if (vraies.length > nbVrai) {
            const retiree = randomChoice(vraies);
            propositions.splice(propositions.indexOf(retiree), 1);
            if (!ajouterFausse()) break;
        } else if (vraies.length < nbVrai && fausses.length > CIBLE_PROPS - nbVrai) {
            const retiree = randomChoice(fausses);
            propositions.splice(propositions.indexOf(retiree), 1);
            if (!ajouterVraie()) break;
        } else if (propositions.length < CIBLE_PROPS) {
            if (vraies.length < nbVrai) { if (!ajouterVraie()) break; }
            else if (!ajouterFausse()) break;
        } else break;
    }

    // Mélanger et préparer le format QCM multi
    const shuffled = shuffle(propositions);
    const allTexts = shuffled.map((p) => p.text);
    const correctTexts = shuffled.filter((p) => p.isVrai).map((p) => p.text);

    return makeQcm(
        allTexts.map(espMilliersTexte),
        correctTexts.map(espMilliersTexte),
        `<p>Parmi les égalités suivantes, lesquelles sont vraies ?</p>`,
        opts(L_QCM_MULTI),
        true,
    );
};

// ═══════════════════════════════════════════════════════════
// 3. SAISIE TEXTE — Taper le résultat
// ═══════════════════════════════════════════════════════════

const FORMULATIONS_SAISIE = [
    (a, c) => `<p>Calculer ${a} × ${c}.</p>`,
    (a, c) => `<p>Donner le résultat de ${a} × ${c}.</p>`,
    (a, c) => `<p>Combien fait ${a} × ${c} ?</p>`,
];

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureSaisie610 = null;

const genMultSaisie = () => {
    let payload = construireMultSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSaisie610; t++) {
        payload = construireMultSaisie();
    }
    derniereSignatureSaisie610 = payload.texte;
    return payload;
};

const construireMultSaisie = () => {
    // 20% faits mémorisés
    if (Math.random() < 0.2) {
        const fait = randomChoice(FAITS_MEMORISES);
        return makeSaisie(
            fait.r,
            randomChoice(FORMULATIONS_SAISIE)(espMilliers(fait.a), fait.c),
            { ...opts(L_SAISIE), strict: false },
        );
    }

    const c = randomChoice(CONSTANTES);
    const a = randFacteur();
    const correct = shiftLeft(a, c.k);

    return makeSaisie(
        correct,
        randomChoice(FORMULATIONS_SAISIE)(espMilliers(a), c.str),
        { ...opts(L_SAISIE), strict: false },
    );
};

// ═══════════════════════════════════════════════════════════
// 4. RELIER — 4 paires "a × c" → résultat
// ═══════════════════════════════════════════════════════════

// Anti-répétition immédiate (revue 2026-08-19, recette standard) — pool
// borné aux 49 facteurs, enrichissement refusé (décision utilisateur).
let derniereSignatureRelier610 = null;

const genMultRelier = () => {
    let payload = construireMultRelier();
    const sig = (p) => p.contenu.pairs.map((x) => x.source).join('|');
    for (let t = 0; t < 8 && sig(payload) === derniereSignatureRelier610; t++) {
        payload = construireMultRelier();
    }
    derniereSignatureRelier610 = sig(payload);
    return payload;
};

const construireMultRelier = () => {
    // Template : MÊME nombre × 4 constantes différentes (× 1, × 0,1, × 0,01, × 0,001).
    // Impossible pour l'élève de deviner par élimination sur la taille —
    // il doit vraiment comprendre l'effet de chaque constante sur la virgule.
    // Le × 1 teste au passage la notion d'identité (multiplier par 1 ne change rien).

    const useDec = Math.random() < 0.3;
    const a = useDec ? randomChoice(POOL_DECIMAUX) : String(randomChoice(POOL_ENTIERS));

    const CONSTANTES_RELIER = [
        { str: '1',     k: 0 },
        { str: '0,1',   k: 1 },
        { str: '0,01',  k: 2 },
        { str: '0,001', k: 3 },
    ];

    const pairs = CONSTANTES_RELIER.map((c) => ({
        source: `${espMilliers(a)} × ${c.str}`,
        target: espMilliers(shiftLeft(a, c.k)),
    }));

    return makeRelier(
        pairs,
        `<p>Relie chaque produit à son résultat.</p>`,
        opts(L_RELIER),
    );
};

// ═══════════════════════════════════════════════════════════
// 5. VRAI/FAUX — 6 lignes 3V/3F, couvrant les 3 constantes
// ═══════════════════════════════════════════════════════════

const genMultVF = () => {
    const lignes = [];
    const bonnes = []; // true = Vrai, false = Faux
    const seen = new Set();

    const push = (text, isVrai) => {
        if (seen.has(text)) return false;
        seen.add(text);
        lignes.push(text);
        bonnes.push(isVrai);
        return true;
    };

    // (V1) Cas canonique famille 1 (entier × 0,1)
    for (let t = 0; t < 20; t++) {
        const a = String(randomChoice(POOL_ENTIERS));
        if (push(`${a} × 0,1 = ${shiftLeft(a, 1)}`, true)) break;
    }

    // (V2) Fait mémorisé entre constantes
    {
        const fait = randomChoice(FAITS_MEMORISES);
        push(`${fait.a} × ${fait.c} = ${fait.r}`, true);
    }

    // (V3) Cas famille 4 ou 6 (décimal × constante OU zéros intercalaires)
    for (let t = 0; t < 20; t++) {
        if (Math.random() < 0.5) {
            const c = randomChoice(CONSTANTES);
            const a = randomChoice(POOL_DECIMAUX);
            if (push(`${a} × ${c.str} = ${shiftLeft(a, c.k)}`, true)) break;
        } else {
            // Zéros intercalaires : entier à 1 chiffre × 0,001
            const a = String(randomChoice([3, 5, 7, 8, 9]));
            if (push(`${a} × 0,001 = ${shiftLeft(a, 3)}`, true)) break;
        }
    }

    // (F1) Surgénéralisation "plus grand" — erreur MAJEURE du cluster
    for (let t = 0; t < 20; t++) {
        const c = randomChoice(CONSTANTES);
        const a = String(randomChoice(POOL_ENTIERS));
        const faux = shiftRight(a, c.k);
        if (sameDecimal(faux, shiftLeft(a, c.k))) continue;
        if (push(`${a} × ${c.str} = ${faux}`, false)) break;
    }

    // (F2) Confusion de rangs
    for (let t = 0; t < 20; t++) {
        const c = randomChoice(CONSTANTES);
        const a = String(randomChoice(POOL_ENTIERS));
        const wrongK = c.k === 1 ? 2 : (c.k === 3 ? 2 : 1);
        const faux = shiftLeft(a, wrongK);
        if (sameDecimal(faux, shiftLeft(a, c.k))) continue;
        if (push(`${a} × ${c.str} = ${faux}`, false)) break;
    }

    // (F3) Addition au lieu de multiplication
    for (let t = 0; t < 20; t++) {
        const c = randomChoice(CONSTANTES);
        const a = String(randomChoice(POOL_ENTIERS));
        const aNum = parseFloat(a.replace(',', '.'));
        const cNum = parseFloat(c.str.replace(',', '.'));
        let addStr = (aNum + cNum).toFixed(3).replace('.', ',').replace(/0+$/, '').replace(/,$/, '');
        if (sameDecimal(addStr, shiftLeft(a, c.k))) continue;
        if (push(`${a} × ${c.str} = ${addStr}`, false)) break;
    }

    // Quota VARIABLE de vraies (revue 2026-08-19) : le 3V/3F constant était
    // comptable — l'élève finissait le tableau par déduction. On vise 2 à 4.
    const nbVrai = randInt(2, 4);
    const retirer = (isVrai) => {
        const idx = lignes.map((l, i) => i).filter((i) => bonnes[i] === isVrai);
        if (!idx.length) return false;
        const i = randomChoice(idx);
        lignes.splice(i, 1);
        bonnes.splice(i, 1);
        return true;
    };
    const ajouter = (isVrai) => {
        for (let t = 0; t < 30; t++) {
            const a = String(randInt(11, 99));
            const c = randomChoice(CONSTANTES);
            const texte = isVrai
                ? `${a} × ${c.str} = ${shiftLeft(a, c.k)}`
                : `${a} × ${c.str} = ${shiftRight(a, c.k)}`;
            if (push(texte, isVrai)) return true;
        }
        return false;
    };
    for (let garde = 0; garde < 10; garde++) {
        const vraiCount = bonnes.filter((b) => b).length;
        if (lignes.length < 6) {
            if (!ajouter(vraiCount < nbVrai)) break;
        } else if (vraiCount > nbVrai) {
            if (!retirer(true) || !ajouter(false)) break;
        } else if (vraiCount < nbVrai) {
            if (!retirer(false) || !ajouter(true)) break;
        } else break;
    }

    // Shuffle
    const indexed = lignes.map((l, i) => ({ l, v: bonnes[i] }));
    const shuffled = shuffle(indexed);
    const finalLignes = shuffled.map((x) => x.l);
    const finalBonnes = shuffled.map((x) => (x.v ? 0 : 1));

    return makeVraiFaux(
        finalLignes.map(espMilliersTexte),
        finalBonnes,
        `<p>Pour chaque égalité, dis si elle est vraie ou fausse.</p>`,
        opts(L_VF),
    );
};

// ═══════════════════════════════════════════════════════════
// 6. TEXTE À TROUS (DRAG) — 4 produits à compléter
// ═══════════════════════════════════════════════════════════

const buildTrouPhrase = (items) =>
    items.map(({ a, c, r }) => `${a} × ${c.str} = {{${r}}}`).join('\n');

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureDrag610 = null;

const genMultTrouDrag = () => {
    let payload = construireMultTrouDrag();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureDrag610; t++) {
        payload = construireMultTrouDrag();
    }
    derniereSignatureDrag610 = payload.contenu.phrase;
    return payload;
};

const construireMultTrouDrag = () => {
    // 2 facteurs libres × 2 constantes = 4 trous
    const items = [];
    const usedResults = new Set();

    const tryAdd = (a, c) => {
        const r = shiftLeft(a, c.k);
        if (usedResults.has(r)) return false;
        usedResults.add(r);
        items.push({ a, c, r });
        return true;
    };

    // Piocher 2 facteurs libres distincts et 2 constantes distinctes
    for (let tries = 0; tries < 40 && items.length < 4; tries++) {
        items.length = 0;
        usedResults.clear();
        const a1 = String(randomChoice(POOL_ENTIERS));
        let a2 = String(randomChoice(POOL_ENTIERS));
        let safety = 0;
        while (a2 === a1 && safety < 10) { a2 = String(randomChoice(POOL_ENTIERS)); safety++; }
        if (a2 === a1) continue;

        const [c1, c2] = shuffle(CONSTANTES).slice(0, 2);
        tryAdd(a1, c1);
        tryAdd(a1, c2);
        tryAdd(a2, c1);
        tryAdd(a2, c2);
    }

    // Fallback déterministe
    if (items.length < 4 || usedResults.size < 4) {
        items.length = 0;
        usedResults.clear();
        tryAdd('45', CONSTANTES[0]); // 4,5
        tryAdd('45', CONSTANTES[1]); // 0,45
        tryAdd('8',  CONSTANTES[0]); // 0,8
        tryAdd('8',  CONSTANTES[2]); // 0,008
    }

    const bonnes = items.map((x) => x.r);
    const phrase = espMilliersHorsTrous(buildTrouPhrase(items));

    // Distracteurs — 3 familles distinctes
    const dist = new Set();

    // (a) Surgénéralisation plus grand — ×10^k sur le premier item
    const d1 = shiftRight(items[0].a, items[0].c.k);
    if (!bonnes.includes(d1)) dist.add(d1);

    // (b) Confusion de rangs — mauvais nombre de rangs sur un autre item
    const item2 = items[items.length - 1];
    const wrongK = item2.c.k === 1 ? 2 : 1;
    const d2 = shiftLeft(item2.a, wrongK);
    if (!bonnes.includes(d2) && !dist.has(d2)) dist.add(d2);

    // (c) Addition au lieu de multiplication
    const itemMid = items[1];
    const aNum = parseFloat(itemMid.a.replace(',', '.'));
    const cNum = parseFloat(itemMid.c.str.replace(',', '.'));
    let d3 = (aNum + cNum).toFixed(3).replace('.', ',').replace(/0+$/, '').replace(/,$/, '');
    if (!bonnes.includes(d3) && !dist.has(d3)) dist.add(d3);

    // Fallback : compléter à 3 distracteurs
    while (dist.size < 3) {
        const c = randomChoice(CONSTANTES);
        const a = String(randomChoice(POOL_ENTIERS));
        const candidate = shiftLeft(a, c.k);
        if (!bonnes.includes(candidate) && !dist.has(candidate)) dist.add(candidate);
        else break;
    }

    return makeTexteTrou(
        phrase,
        `<p>Complète en faisant glisser les bonnes valeurs dans les cases.</p>`,
        { ...opts(L_TROU_DRAG), mode: 'drag', distracteurs: [...dist].slice(0, 3) },
    );
};

// ═══════════════════════════════════════════════════════════
// 7. TEXTE À TROUS (SAISIE) — 4 produits à taper
// ═══════════════════════════════════════════════════════════

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureTrouS610 = null;

const genMultTrouSaisie = () => {
    let payload = construireMultTrouSaisie();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureTrouS610; t++) {
        payload = construireMultTrouSaisie();
    }
    derniereSignatureTrouS610 = payload.contenu.phrase;
    return payload;
};

const construireMultTrouSaisie = () => {
    // Mêmes items que drag, mais mode saisie (pas de distracteurs)
    const items = [];
    const usedResults = new Set();

    const tryAdd = (a, c) => {
        const r = shiftLeft(a, c.k);
        if (usedResults.has(r)) return false;
        usedResults.add(r);
        items.push({ a, c, r });
        return true;
    };

    // On privilégie une couverture diversifiée (3 constantes + famille 5)
    for (let tries = 0; tries < 40 && items.length < 4; tries++) {
        items.length = 0;
        usedResults.clear();

        // Slot 1 : décimal × 0,01 (famille 4)
        const dec = randomChoice(POOL_DECIMAUX);
        tryAdd(dec, CONSTANTES[1]);

        // Slot 2 : petit entier × 0,001 (famille 6, zéros intercalaires)
        const small = String(randomChoice([3, 5, 7, 8, 9]));
        tryAdd(small, CONSTANTES[2]);

        // Slot 3 : entier avec zéro en queue × 0,1 (famille 2, retombée entier)
        const tens = String(randomChoice([100, 120, 240, 300, 500, 750, 1000, 2400]));
        tryAdd(tens, CONSTANTES[0]);

        // Slot 4 : fait mémorisé (famille 5)
        const fait = randomChoice(FAITS_MEMORISES);
        const cObj = CONSTANTES.find((x) => x.str === fait.c);
        tryAdd(fait.a, cObj);

        if (items.length === 4 && usedResults.size === 4) break;
    }

    // Fallback déterministe
    if (items.length < 4 || usedResults.size < 4) {
        items.length = 0;
        usedResults.clear();
        tryAdd('12,5', CONSTANTES[1]);   // 0,125
        tryAdd('7',    CONSTANTES[2]);   // 0,007
        tryAdd('240',  CONSTANTES[0]);   // 24
        tryAdd('0,1',  CONSTANTES[0]);   // 0,01
    }

    const phrase = espMilliersHorsTrous(buildTrouPhrase(items));

    return makeTexteTrou(
        phrase,
        `<p>Complète chaque égalité en tapant le résultat.</p>`,
        { ...opts(L_TROU_SAISIE), mode: 'saisie', strict: false },
    );
};

// ═══════════════════════════════════════════════════════════
// 8. CONVERSION DE MESURES — Template contextualisé
// ═══════════════════════════════════════════════════════════
//
// Idée pédagogique : le rappel "1 cg = 0,01 g" contextualise le ×0,01.
// L'élève comprend POURQUOI on multiplie (parce que 1 cg c'est 0,01 g)
// et révise ses conversions d'unités en même temps.
//
// On ne garde QUE les conversions "petite unité → grande unité" qui
// correspondent à l'attendu 610 (×0,1 / ×0,01 / ×0,001).
//
// 2 formats :
//   - conversion_texte             : saisie simple + suffixe d'unité (1 conv.)
//   - conversion_texte_trou_saisie : 4 conversions en texte à trous

/** Pool de conversions filtrées pour correspondre à ×0,1 / ×0,01 / ×0,001. */
const CONVERSIONS = [
    // Masses ×0,1
    { from: 'mg',  to: 'cg', quantity: 'masse', factor: '0,1',   k: 1 },
    { from: 'cg',  to: 'dg', quantity: 'masse', factor: '0,1',   k: 1 },
    { from: 'dg',  to: 'g',  quantity: 'masse', factor: '0,1',   k: 1 },
    { from: 'g',   to: 'dag',quantity: 'masse', factor: '0,1',   k: 1 },
    { from: 'dag', to: 'hg', quantity: 'masse', factor: '0,1',   k: 1 },
    { from: 'hg',  to: 'kg', quantity: 'masse', factor: '0,1',   k: 1 },
    // Masses ×0,01
    { from: 'mg',  to: 'dg', quantity: 'masse', factor: '0,01',  k: 2 },
    { from: 'cg',  to: 'g',  quantity: 'masse', factor: '0,01',  k: 2 },
    { from: 'g',   to: 'hg', quantity: 'masse', factor: '0,01',  k: 2 },
    { from: 'dag', to: 'kg', quantity: 'masse', factor: '0,01',  k: 2 },
    // Masses ×0,001
    { from: 'mg',  to: 'g',  quantity: 'masse', factor: '0,001', k: 3 },
    { from: 'g',   to: 'kg', quantity: 'masse', factor: '0,001', k: 3 },
    { from: 'kg',  to: 't',  quantity: 'masse', factor: '0,001', k: 3 },

    // Longueurs ×0,1
    { from: 'mm',  to: 'cm', quantity: 'longueur', factor: '0,1',   k: 1 },
    { from: 'cm',  to: 'dm', quantity: 'longueur', factor: '0,1',   k: 1 },
    { from: 'dm',  to: 'm',  quantity: 'longueur', factor: '0,1',   k: 1 },
    { from: 'm',   to: 'dam',quantity: 'longueur', factor: '0,1',   k: 1 },
    { from: 'dam', to: 'hm', quantity: 'longueur', factor: '0,1',   k: 1 },
    { from: 'hm',  to: 'km', quantity: 'longueur', factor: '0,1',   k: 1 },
    // Longueurs ×0,01
    { from: 'mm',  to: 'dm', quantity: 'longueur', factor: '0,01',  k: 2 },
    { from: 'cm',  to: 'm',  quantity: 'longueur', factor: '0,01',  k: 2 },
    { from: 'm',   to: 'hm', quantity: 'longueur', factor: '0,01',  k: 2 },
    { from: 'dam', to: 'km', quantity: 'longueur', factor: '0,01',  k: 2 },
    // Longueurs ×0,001
    { from: 'mm',  to: 'm',  quantity: 'longueur', factor: '0,001', k: 3 },
    { from: 'dm',  to: 'hm', quantity: 'longueur', factor: '0,001', k: 3 },
    { from: 'm',   to: 'km', quantity: 'longueur', factor: '0,001', k: 3 },
];

// ─── Générateur A : Saisie simple + suffixe d'unité ───

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureConv610 = null;

const genMultConversionSaisie = () => {
    let payload = construireMultConversionSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureConv610; t++) {
        payload = construireMultConversionSaisie();
    }
    derniereSignatureConv610 = payload.texte;
    return payload;
};

const construireMultConversionSaisie = () => {
    const quantity = randomChoice(['masse', 'longueur']);
    const pool = CONVERSIONS.filter((c) => c.quantity === quantity);
    const conv = randomChoice(pool);
    const facteur = randFacteur();
    const resultat = shiftLeft(facteur, conv.k);

    const texte = `<p>On rappelle que <strong>1 ${conv.from} = ${conv.factor} ${conv.to}</strong>.</p><p>Convertis : ${espMilliers(facteur)} ${conv.from} = ?</p>`;

    return makeSaisie(resultat, texte, {
        ...opts(L_CONVERSION),
        strict: false,
        suffixe: conv.to,
    });
};

// ─── Générateur B : Texte à trous saisie (4 conversions, 2 pairs) ───

const genMultConversionTrouSaisie = () => {
    const quantity = randomChoice(['masse', 'longueur']);
    const pool = CONVERSIONS.filter((c) => c.quantity === quantity);

    // 2 pairs distinctes (from/to différents) pour varier sans trop complexifier
    const pair1 = randomChoice(pool);
    let pair2 = randomChoice(pool);
    let safety = 0;
    while ((pair2.from === pair1.from && pair2.to === pair1.to) && safety < 20) {
        pair2 = randomChoice(pool);
        safety++;
    }

    // 2 conversions par pair → 4 au total
    const items = [];
    for (const pair of [pair1, pair2]) {
        for (let i = 0; i < 2; i++) {
            let safety2 = 0;
            let facteur;
            do {
                facteur = randFacteur();
                safety2++;
            } while (items.some((it) => it.facteur === facteur && it.pair === pair) && safety2 < 20);
            items.push({ pair, facteur, resultat: shiftLeft(facteur, pair.k) });
        }
    }

    const shuffled = shuffle(items);

    const rappel = `<p>On rappelle que <strong>1 ${pair1.from} = ${pair1.factor} ${pair1.to}</strong> et <strong>1 ${pair2.from} = ${pair2.factor} ${pair2.to}</strong>.</p>`;
    const texte = `${rappel}<p>Complète les conversions suivantes :</p>`;

    const phrase = shuffled
        .map((it) => `${espMilliers(it.facteur)} ${it.pair.from} = {{${it.resultat}}} ${it.pair.to}`)
        .join('\n');

    return makeTexteTrou(phrase, texte, {
        ...opts(L_CONVERSION),
        mode: 'saisie',
        strict: false,
        distracteurs: [],
    });
};

// ─── Registre ───
const GENERATORS = {
    multiplier_01_qcm_unique: genMultQcm,
    multiplier_01_qcm_multiple: genMultQcmMultiple,
    multiplier_01_texte: genMultSaisie,
    multiplier_01_relier: genMultRelier,
    multiplier_01_tableau_vf: genMultVF,
    multiplier_01_texte_trou_drag: genMultTrouDrag,
    multiplier_01_texte_trou_saisie: genMultTrouSaisie,
    conversion_texte: genMultConversionSaisie,
    conversion_texte_trou_saisie: genMultConversionTrouSaisie,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'multiplier_01', label: 'Multiplier par 0,1 / 0,01 / 0,001', formatsList: buildFormats('multiplier_01') },
    // Revue 2026-08-25 (choix prof) : la catégorie conversion est MASQUÉE du
    // catalogue en entier (conversion_texte puis conversion_texte_trou_saisie).
    // Générateurs conservés dans GENERATORS (les questions en banque se
    // régénèrent) ; décommenter pour la faire réapparaître.
    // { id: 'conversion', label: 'Convertir des mesures (masses et longueurs)', formatsList: buildFormats('conversion') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
